import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { avatarSrc, mediaSrc, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { OnlineDot } from "../components/OnlineDot";
import { ConnectionsModal } from "../components/ConnectionsModal";
import { PushToggle } from "../components/PushControls";
import { CoverControls } from "../components/CoverControls";
import { ProfileSongPlayer, ProfileSongEditor } from "../components/ProfileSong";
import { ActiveText, ActivityToggle } from "../components/ActivityStatus";
import MediaCard from "../components/MediaCard";
import { useFeedCache, readFeedCache, restoreScroll } from "../hooks/useFeedCache";
import { Loader2, UserPlus, UserCheck, Pencil, Check, X, Camera, Download, Shield, Grid3x3, UserSquare, MessageCircle, Repeat2, Archive, Bookmark } from "lucide-react";
import { toast } from "sonner";
import { VerifiedBadge } from "../components/VerifiedBadge";
import { DonorBadge } from "../components/DonorBadge";
import { VerificationRequest } from "../components/VerificationRequest";

export default function Profile() {
  const { username } = useParams();
  const cacheKey = `profile:${username}`;
  const nav = useNavigate();
  const { user, setUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [connKind, setConnKind] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [uname, setUname] = useState("");
  const [bio, setBio] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarRef = useRef();
  const restored = useRef(false);
  const [tab, setTab] = useState("posts");
  const [tagged, setTagged] = useState(null);
  const [reblogs, setReblogs] = useState(null);
  const [bookmarks, setBookmarks] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/users/${username}`);
      setProfile(data);
      setDisplayName(data.display_name || "");
      setUname(data.username || "");
      setBio(data.bio || "");
      const { data: m } = await api.get("/media", { params: { author: username, limit: 60 } });
      setItems(m);
    } catch {
      setProfile(false);
    } finally {
      setLoading(false);
    }
  }, [username]);

  useEffect(() => { restored.current = false; setTab("posts"); setTagged(null); setReblogs(null); setBookmarks(null); load(); }, [load]);
  useEffect(() => {
    if (tab !== "tagged" || tagged) return;
    api.get("/media", { params: { tagged: username, limit: 60 } }).then(({ data }) => setTagged(data)).catch(() => setTagged([]));
  }, [tab, tagged, username]);
  useEffect(() => {
    if (tab !== "reblogs" || reblogs) return;
    api.get(`/users/${username}/reblogs`).then(({ data }) => setReblogs(data)).catch(() => setReblogs([]));
  }, [tab, reblogs, username]);
  useEffect(() => {
    if (tab !== "bookmarks" || bookmarks) return;
    api.get("/saved").then(({ data }) => setBookmarks(data)).catch(() => setBookmarks([]));
  }, [tab, bookmarks]);
  const extra = { tagged, reblogs, bookmarks };
  const shown = tab === "posts" ? items : (extra[tab] || []);
  const EMPTY = { posts: "No posts yet", tagged: "No tagged posts yet", reblogs: "No reblogs yet", bookmarks: "Nothing bookmarked yet — tap the bookmark on any post" };
  useEffect(() => {
    if (!restored.current && !loading && items.length) {
      const c = readFeedCache(cacheKey);
      if (c?.scrollY) restoreScroll(c.scrollY);
      restored.current = true;
    }
  }, [loading, items.length, cacheKey]);
  useFeedCache(cacheKey, items);

  const follow = async () => {
    if (!user) return;
    const { data } = await api.post(`/users/${profile.id}/follow`);
    setProfile((p) => ({ ...p, is_following: data.following, followers: data.followers }));
  };

  const [opening, setOpening] = useState(false);
  const message = async () => {
    setOpening(true);
    try {
      const { data } = await api.post("/conversations", { type: "dm", member_ids: [profile.id] });
      nav(`/chat/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setOpening(false); }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", { display_name: displayName, bio, username: uname });
      setUser(data.user);
      setEditing(false);
      toast.success("Profile updated");
      if (data.user.username !== username) {
        nav(`/u/${data.user.username}`, { replace: true });
      } else {
        setProfile((p) => ({ ...p, display_name: data.user.display_name, bio: data.user.bio }));
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image")) return toast.error("Avatar must be an image");
    setUploadingAvatar(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/auth/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUser(data.user);
      setProfile((p) => ({ ...p, avatar_url: data.avatar_url }));
      toast.success("Avatar updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingAvatar(false);
    }
  };

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-[#0066FF]" size={32} /></div>;
  if (!profile) return <div className="text-center py-32 font-heading text-3xl text-[#2A2A2E]">User not found</div>;

  const isMe = user && user.id === profile.id;
  const bannerItem = items.find((i) => i.thumb_url || i.url);
  const bannerSrc = bannerItem ? mediaSrc(bannerItem.thumb_url || bannerItem.url) : null;

  return (
    <div>
      {/* cinematic banner */}
      <div className="relative h-44 sm:h-60 overflow-hidden" data-testid="profile-banner">
        {profile.cover_url ? (
          <img data-testid="profile-cover-img" src={avatarSrc(profile.cover_url)} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : bannerSrc ? (
          <img src={bannerSrc} alt="" className="absolute inset-0 w-full h-full object-cover scale-110 blur-[2px] opacity-60" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#0066FF]/40 via-[#0F1117] to-[#00E5FF]/20" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#08090C] via-[#08090C]/40 to-transparent" />
        {isMe && <CoverControls profile={profile} setProfile={setProfile} setUser={setUser} />}
        {profile.profile_song && <ProfileSongPlayer key={profile.profile_song.url} song={profile.profile_song} />}
      </div>

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 relative -mt-16 sm:-mt-20 z-10">
        <div className="flex flex-col sm:flex-row items-start sm:items-end gap-5">
          <div className="relative shrink-0">
            {profile.avatar_url ? (
              <img data-testid="profile-avatar" src={avatarSrc(profile.avatar_url)} alt={profile.username} className="w-28 h-28 sm:w-32 sm:h-32 object-cover rounded-2xl ring-4 ring-[#08090C] bg-[#161922]" />
            ) : (
              <div data-testid="profile-avatar" className="w-28 h-28 sm:w-32 sm:h-32 rounded-2xl ring-4 ring-[#08090C] bg-[#0066FF] flex items-center justify-center font-heading text-white text-5xl">{profile.username[0].toUpperCase()}</div>
            )}
            <OnlineDot username={profile.username} size={20} className="-bottom-1 -right-1 ring-4 ring-[#08090C]" />
            {isMe && editing && (
              <>
                <button data-testid="avatar-upload-btn" onClick={() => avatarRef.current?.click()} disabled={uploadingAvatar} className="absolute inset-0 rounded-2xl bg-black/70 flex items-center justify-center text-white hover:bg-black/85 transition-colors">
                  {uploadingAvatar ? <Loader2 size={22} className="animate-spin" /> : <Camera size={22} />}
                </button>
                <input ref={avatarRef} type="file" accept="image/*" className="hidden" data-testid="avatar-file-input" onChange={(e) => uploadAvatar(e.target.files[0])} />
              </>
            )}
          </div>

          <div className="flex-1 w-full pb-1">
            {editing ? (
              <div className="space-y-2 max-w-md">
                <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} data-testid="edit-displayname" placeholder="Display name" className="w-full bg-[#161922] border border-white/10 text-white text-2xl font-heading px-3 py-1.5 focus:outline-none focus:border-[#0066FF] rounded-xl" />
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0066FF] font-mono text-sm">@</span>
                  <input value={uname} onChange={(e) => setUname(e.target.value)} data-testid="edit-username" placeholder="username" className="w-full bg-[#161922] border border-white/10 text-white font-mono text-sm pl-7 pr-3 py-2 focus:outline-none focus:border-[#0066FF] rounded-xl" />
                </div>
              </div>
            ) : (
              <>
                <h1 data-testid="profile-name" className="font-heading text-3xl sm:text-4xl tracking-tight text-white leading-none flex items-center gap-2">{profile.display_name}<VerifiedBadge show={profile.verified} size={26} testId="profile-verified-badge" /><DonorBadge tier={profile.donor_tier} size={24} testId="profile-donor-badge" /></h1>
                <p className="font-mono text-xs text-[#0066FF] mt-1.5">@{profile.username}<ActiveText username={profile.username} /></p>
              </>
            )}
            {editing ? (
              <textarea value={bio} onChange={(e) => setBio(e.target.value)} data-testid="edit-bio" rows={2} placeholder="Bio" className="mt-2 w-full max-w-md bg-[#161922] border border-white/10 text-white text-sm px-3 py-2 focus:outline-none focus:border-[#0066FF] rounded-xl resize-none" />
            ) : profile.bio ? (
              <p className="text-[#9CA3AF] text-sm mt-3 max-w-md leading-relaxed">{profile.bio}</p>
            ) : null}
            <div className="flex items-center gap-6 mt-4 font-mono text-xs">
              <span className="text-white font-semibold">{profile.media_count} <span className="text-[#6B7280] font-normal">POSTS</span></span>
              <button data-testid="followers-btn" onClick={() => setConnKind("followers")} className="text-white font-semibold hover:text-[#00E5FF] transition-colors">{profile.followers} <span className="text-[#6B7280] font-normal">FOLLOWERS</span></button>
              <button data-testid="following-btn" onClick={() => setConnKind("following")} className="text-white font-semibold hover:text-[#00E5FF] transition-colors">{profile.following} <span className="text-[#6B7280] font-normal">FOLLOWING</span></button>
              <ConnectionsModal userId={profile.id} kind={connKind} onClose={() => setConnKind(null)} />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pb-1">
            {isMe ? (
              editing ? (
                <>
                  <button data-testid="save-profile-btn" onClick={saveProfile} disabled={saving} className="flex items-center gap-2 bg-[#0066FF] text-white font-semibold text-sm px-5 py-2.5 rounded-lg hover:bg-[#0055CC] transition-colors disabled:opacity-50">{saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save</button>
                  <button data-testid="cancel-profile-btn" onClick={() => { setEditing(false); setDisplayName(profile.display_name); setUname(profile.username); setBio(profile.bio || ""); }} className="flex items-center gap-2 border border-white/10 text-[#9CA3AF] font-semibold text-sm px-4 py-2.5 rounded-lg hover:text-white transition-colors"><X size={15} /></button>
                </>
              ) : (
                <>
                  <button data-testid="edit-profile-btn" onClick={() => setEditing(true)} className="flex items-center gap-2 border border-white/10 text-white font-semibold text-sm px-5 py-2.5 rounded-lg hover:border-[#0066FF] transition-colors"><Pencil size={14} /> Edit</button>
                  <Link to="/import" data-testid="profile-import-link" className="flex items-center gap-2 border border-[#0066FF]/40 text-[#93c5fd] font-semibold text-sm px-4 py-2.5 rounded-lg hover:bg-[#0066FF]/10 hover:border-[#0066FF] transition-colors"><Download size={14} /> Import</Link>
                  {!profile.verified && <VerificationRequest />}
                  <ActivityToggle user={user} setUser={setUser} />
                  <PushToggle />
                  <ProfileSongEditor song={profile.profile_song} onChange={(profile_song) => setProfile((p) => ({ ...p, profile_song }))} />
                  {(user.role === "admin" || user.role === "moderator") && (
                    <Link to="/admin" data-testid="profile-admin-link" className="flex items-center gap-2 border border-white/10 text-[#9CA3AF] font-semibold text-sm px-4 py-2.5 rounded-lg hover:text-white hover:border-[#0066FF] transition-colors"><Shield size={14} /> {user.role === "admin" ? "Admin" : "Mod"}</Link>
                  )}
                </>
              )
            ) : user ? (
              <div className="flex items-center gap-2">
                <button data-testid="follow-btn" onClick={follow} className={`flex items-center gap-2 font-semibold text-sm px-5 py-2.5 rounded-lg transition-colors border ${profile.is_following ? "border-white/10 text-white hover:border-[#0066FF]" : "bg-[#0066FF] border-[#0066FF] text-white hover:bg-[#0055CC]"}`}>
                  {profile.is_following ? <><UserCheck size={15} /> Following</> : <><UserPlus size={15} /> Follow</>}
                </button>
                <button data-testid="message-btn" onClick={message} disabled={opening} className="flex items-center gap-2 font-semibold text-sm px-5 py-2.5 rounded-lg border border-white/10 text-white hover:border-[#0066FF] transition-colors disabled:opacity-50">
                  {opening ? <Loader2 size={15} className="animate-spin" /> : <MessageCircle size={15} />} Message
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-16">
        <div className="flex border-b border-white/[0.08] mb-6" data-testid="profile-tabs" role="tablist">
          {[["posts", "Posts", Grid3x3], ["reblogs", "Reblogs", Repeat2], ["tagged", "Tagged", UserSquare], ["archive", "Archive", Archive], ...(isMe ? [["bookmarks", "Bookmarks", Bookmark]] : [])].map(([k, label, Ic]) => (
            <button key={k} role="tab" aria-selected={tab === k} data-testid={`profile-tab-${k}`} onClick={() => (k === "archive" ? nav(`/archive?user=${profile.username}`) : setTab(k))}
              className={`flex-1 sm:flex-none sm:px-4 min-w-0 flex items-center justify-center gap-1.5 sm:gap-2 pb-3 -mb-px border-b-2 text-[12px] sm:text-sm font-semibold transition-colors ${tab === k ? "border-[#0066FF] text-white" : "border-transparent text-[#8A8D91] hover:text-white"}`}>
              <Ic size={16} className="hidden sm:block shrink-0" /> <span className="truncate">{label}</span>
            </button>
          ))}
        </div>
        {tab !== "posts" && extra[tab] === null ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#0066FF]" size={22} /></div>
        ) : shown.length === 0 ? (
          <p data-testid={`profile-empty-${tab}`} className="text-center py-20 font-heading text-2xl text-[#2A2A2E]">{EMPTY[tab]}</p>
        ) : (
          <div className="columns-2 sm:columns-3 lg:columns-4 xl:columns-5 gap-2 sm:gap-3" data-testid={tab === "posts" ? "profile-media-grid" : `profile-${tab}-grid`}>
            {shown.map((item, i) => <MediaCard key={item.id} item={item} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
